import { useMemo } from "react";
import ReactImageMagnify from 'react-image-magnify';
import { formatterCreateAt, FlagRegionByIso } from "components/Function";
import { Line } from "react-chartjs-2";
import { Link } from "react-router-dom";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const useColumnTableProductsByShop = (heightRow = 100, productTrendData, dayRanger) => {
    const columns = useMemo(() => {
        const titleNd = dayRanger === "1" ? "Yesterday" : (dayRanger || "30") + " days";
        return [
            {
                field: "_id",
                headerName: "",
                sortable: false,
                editable: false,
                cellClass: "centered-cell",
                width: 10,
                cellRenderer: ({ rowIndex, value: cell, data: row }) => rowIndex + 1,
            },
            {
                field: "product_name",
                headerName: "Product",
                sortable: false,
                editable: false,
                autoHeight: true,
                cellClass: "overflow-visible",
                width: 500,
                cellRenderer: ({ data: row, cell }) => (
                    <div className="d-flex align-items-center p-4" style={{ height: heightRow - 1 + "px" }}>
                        <div className="symbol symbol-60px symbol-lg-80px symbol-fixed position-relative me-3">
                            {/* <Link to={row.cover_url} target="_blank"> */}
                            <ReactImageMagnify {...{ smallImage: { alt: "", width: 50, height: 50, src: row.cover_url }, largeImage: { src: row.cover_url, width: 600, height: 600 }, enlargedImageContainerDimensions: { width: '600%', height: '600%' }, enlargedImageContainerStyle: { "zIndex": 99 }, imageClassName: "overflow-hidden al-border-radius-8" }} />
                            {/* </Link> */}
                        </div>
                        <div
                            className="d-flex flex-column lh-lg gap-2 w-100px"
                            style={{
                                overflow: "hidden",
                                flex: "1",
                            }}
                        >
                            <span
                                data-tooltip-id="my-tooltip"
                                data-tooltip-content={row.product_name}
                                className="mb-0"
                                style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flex: "1",
                                }}
                            >
                                <Link
                                    role="button"
                                    target="_blank"
                                    to={`/admin/spy-advanced/products/${row.product_id}`}
                                >
                                    {row.product_name}
                                </Link>
                            </span>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span
                                    data-tooltip-id="my-tooltip"
                                    data-tooltip-content={"Price"}
                                    className="fs-6 text-danger mb-0"
                                >
                                    {row.real_price}
                                </span>
                                {row.commission && (
                                    <span
                                        data-tooltip-id="my-tooltip"
                                        data-tooltip-content={"Commission"}
                                        className="badge bg-warning text-white rounded-pill"
                                    >
                                        {row.commission}
                                    </span>
                                )}
                            </div>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                {row.region?.key ? (
                                    <FlagRegionByIso name={row.region.name} iso={row.region.key} />
                                ) : (
                                    ""
                                )}
                                <span className="badge bg-info text-white rounded-pill">{row.category}</span>
                                <span
                                    data-tooltip-id="my-tooltip"
                                    data-tooltip-content={"Product Experience Points"}
                                    className="badge bg-info text-white"
                                >
                                    {row.product_rating}
                                </span>
                                <span
                                    data-tooltip-id="my-tooltip"
                                    data-tooltip-content={"Comments"}
                                    className="badge bg-info text-white"
                                >
                                    {row.review_count}
                                </span>
                                {Boolean(row.is_free_shipping) && (
                                    <i
                                        data-tooltip-id="my-tooltip"
                                        data-tooltip-content={"Free Shipping"}
                                        className="fas fa-shipping-fast fs-3"
                                    ></i>
                                )}
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                field: "sold_count",
                headerName: titleNd + " Sales",
                headerClass: "text-info",
                cellClass: "centered-cell",
                sortable: false,
                cellRenderer: ({ value: cell, data: row }) => <span className="text-danger">{row.sold_count}</span>,
            },
            {
                field: "sale_trend",
                headerName: "Sales Trend",
                cellClass: "centered-cell",
                sortable: false,
                width: 150,
                cellRenderer: ({ data: row }) => {
                    const data = productTrendData?.data?.[row?.product_id];

                    if (!data) return;
                    const chartData = {
                        labels: data.map((item) => item.axis_x),
                        datasets: [
                            {
                                label: "Daily Sales",
                                data: data.map((item) => item.axis_y),
                                borderColor: "#5B8FF9",
                                borderWidth: 3,
                                fill: false,
                                tension: 0.5,
                                pointRadius: 1,
                                pointBackgroundColor: "#5B8FF9",
                                pointBorderWidth: 0,
                            },
                        ],
                    };

                    return (
                        <div
                            className="d-flex align-items-center p-4"
                            style={{ height: heightRow - 1 + "px", width: "120px" }}
                        >
                            <div style={{ height: "100%", width: "100%" }}>
                                <Line
                                    data={chartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            tooltip: {
                                                displayColors: false,
                                                callbacks: {
                                                    label: function (tooltipItem) {
                                                        const saleData = tooltipItem.raw;
                                                        return `Sales: ${saleData / 1000}K`;
                                                    },
                                                },
                                            },
                                            legend: {
                                                display: false,
                                            },
                                            datalabels: {
                                                display: false,
                                            },
                                            title: {
                                                display: false,
                                            },
                                        },
                                        scales: {
                                            x: {
                                                display: false,
                                                grid: {
                                                    display: false,
                                                },
                                            },
                                            y: {
                                                display: false,
                                                grid: {
                                                    display: false,
                                                },
                                            },
                                        },
                                        elements: {
                                            line: {
                                                borderWidth: 0,
                                            },
                                            point: {
                                                borderWidth: 0,
                                            },
                                        },
                                        layout: {
                                            padding: {
                                                left: 10,
                                                right: 10,
                                                top: 10,
                                                bottom: 10,
                                            },
                                        },
                                        interaction: {
                                            mode: "nearest",
                                            intersect: false,
                                        },
                                    }}
                                    height={heightRow - 1}
                                    width={120}
                                />
                            </div>
                        </div>
                    );
                },
            },
            {
                field: "total_sale_cnt",
                headerName: "Total Sales",
                cellClass: "centered-cell",
                sortable: false,
            },
            {
                field: "total_gmv_amt",
                headerName: titleNd + " GMV",
                cellClass: "centered-cell",
                sortable: false,
            },
            {
                field: "total_sale_gmv_amt",
                headerName: "Total GMV",
                cellClass: "centered-cell",
                sortable: false,
            },
            {
                field: "influencers_count",
                headerName: "Influencers",
                cellClass: "centered-cell",
                sortable: false,
            },
            {
                field: "videos_count",
                headerName: "Videos",
                cellClass: "centered-cell",
                sortable: false,
            },
            {
                field: "lives_count",
                headerName: "Live",
                cellClass: "centered-cell",
                sortable: false,
            },
            {
                field: "first_time",
                headerName: "Shelf Time",
                cellClass: "centered-cell",
                sortable: false,
                cellRenderer: ({ data: row }) => formatterCreateAt(row?.first_time * 1000)()
            },
            {
                field: "-",
                headerName: "Actions",
                editable: false,
                cellClass: "centered-cell",
                width: 140,
                // pinned: "right",
                cellRenderer: ({ data: row }) => (
                    <>
                        <Link
                            role="button"
                            to={`/admin/spy-advanced/products/${row.product_id}`}
                            className="btn btn-icon btn-bg-light btn-active-color-warning btn-sm me-2"
                            data-tooltip-id="my-tooltip"
                            target="_blank"
                        >
                            <i className="fa fa-search fs-2"></i>
                        </Link>
                    </>
                ),
            },
        ];
    }, [heightRow, productTrendData, dayRanger]);

    return columns;
};

export default useColumnTableProductsByShop;
